import { useState } from 'react';

// 会话来源（**点击时读取**）：仅使用当前控制台登录的会话（integral_token）。
// 刻意**不回退**到 integral_identity_session（Portal 管理页登录的残留）——
// 那可能属于此前登录的另一位管理员；要求后台始终对应当前控制台用户，
// 控制台退出后入口页即不可用（符合"后台操作人 = 当前控制台用户"的约定）。
// 旧 users 表账号的 token 不是 identity 会话，会在后台侧被拒绝
//（服务端校验角色 + 会话，此处不判断）。
function resolveSession(): string {
  try {
    return localStorage.getItem('integral_token') || '';
  } catch {
    return '';
  }
}

// 网站管理（官网后台）统一入口。
//
// 职责边界：Integral 负责员工登录、网站编辑权限与操作记录；
// 官网 CMS 后台（独立服务）负责页面 / 博客 / 媒体 / 站点设置 / 发布。
//
// 会话交接：点击「打开」时，本页先把 Integral 会话 POST 到官网后台的
// /api/handoff 换取**短期单次兑换码**（30 秒），再以 URL 片段 #h=<code>
// 打开后台；后台兑换出自己的会话令牌，CMS 页面不持有 Integral 原始会话。
// 后台地址使用**精确白名单**（可用构建变量覆盖；生产品牌域名须显式列出）。

const URL_KEY = 'website_admin_url';
const DEFAULT_URL = 'http://127.0.0.1:8091/';

// 受信后台地址：精确 origin 白名单（协议+主机+端口完全一致才放行）。
// 可用构建期变量 VITE_WEBSITE_ADMIN_TRUSTED_ORIGINS 覆盖（逗号分隔），
// 生产品牌域名的后台地址必须在此显式配置，不做通配符放行。
const TRUSTED_ORIGINS: string[] = (import.meta.env.VITE_WEBSITE_ADMIN_TRUSTED_ORIGINS as string | undefined ??
  'http://127.0.0.1:8091,http://localhost:8091,https://integral-test.mundolasertec.com.ar,https://integral.mundolasertec.com.ar')
  .split(',')
  .map((s) => s.trim().replace(/\/+$/, ''))
  .filter(Boolean);

function isTrustedAdminUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    const origin = u.origin;
    const isLoopbackHttp = u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]');
    return TRUSTED_ORIGINS.includes(origin) && (isLoopbackHttp || u.protocol === 'https:');
  } catch {
    return false;
  }
}

export function WebsiteAdminPage() {
  const [url, setUrl] = useState<string>(() => {
    try { return localStorage.getItem(URL_KEY) || DEFAULT_URL; } catch { return DEFAULT_URL; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setError(null);
    const trimmed = url.trim().replace(/\/+$/, '') + '/';
    if (!isTrustedAdminUrl(trimmed)) {
      setError('后台地址不在受信范围内（仅允许本机 http 或 *.mundolasertec.com.ar 的 https），防止会话交给不可信站点');
      return;
    }
    // 点击时读取：保证用的是**当前**控制台登录用户的会话，
    // 不受此前 Portal 管理页登录残留的其它管理员会话影响。
    const session = resolveSession();
    if (!session) {
      setError('尚未登录 Integral，请先登录');
      return;
    }
    setBusy(true);
    // 弹窗策略：window.open 必须在用户手势的同步调用栈内执行。
    // 注意不能用 features 参数带 noopener —— 那会使返回值为 null（即使窗口
    // 已成功打开，见 MDN），导致误判"弹窗被拦截"。正确做法：同步打开取得
    // 句柄，立即断开 opener（等效 noopener 且保留句柄），再执行异步交接。
    const win = window.open('about:blank', '_blank');
    if (!win) {
      setError('浏览器拦截了新标签页，请允许本站弹出窗口后重试');
      setBusy(false);
      return;
    }
    try {
      win.opener = null;
      // 用 Integral 会话换取短期单次兑换码（30 秒）；无权角色此处即被拒绝
      const res = await fetch(`${trimmed}api/handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        win.close();
        setError(d.error || `后台返回 ${res.status}`);
        return;
      }
      win.location.href = `${trimmed}#h=${d.code}`;
    } catch {
      win.close();
      setError('无法连接官网后台（检查地址与服务是否启动）');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: '0 20px', lineHeight: 1.8 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6 }}>网站管理</h1>
      <p style={{ color: '#64748b', marginBottom: 20 }}>
        统一管理两个官网：西语站（pinnaclelaser.com.ar）与中文站（pinnalaser.com）。
        发布采用「编辑 → 预览 → 发布」，保留历史版本可回滚；所有操作记入审计日志。
      </p>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 4 }}>
            官网后台地址（须在受信白名单内：本机开发地址或部署环境中显式配置的精确 origin）
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
            placeholder="http://127.0.0.1:8091/"
          />
        </div>
        <button
          onClick={open}
          disabled={busy}
          style={{ background: busy ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, cursor: busy ? 'wait' : 'pointer' }}
        >
          {busy ? '建立会话中…' : '打开网站管理后台 ↗'}
        </button>
        {error && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13.5 }}>{error}</div>}
      </div>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, fontSize: 13.5, color: '#475569' }}>
        <b style={{ color: '#0f172a' }}>权限与安全说明</b>
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          <li>交接使用短期（30 秒）单次兑换码：URL 片段即使被记录也无法重放；CMS 页面不持有 Integral 原始会话。</li>
          <li>后台地址须命中精确 origin 白名单（构建变量 VITE_WEBSITE_ADMIN_TRUSTED_ORIGINS 可覆盖），生产品牌域名显式配置。</li>
          <li>后台对每个写接口都会用服务令牌向 core-api 校验会话与角色（默认仅 company_admin，复验周期 ≤ 60 秒）。</li>
          <li>后台令牌有绝对有效期与空闲过期，可随时注销；上游会话被撤销后自动失效。</li>
          <li>后台内的所有保存 / 上传 / 删除 / 发布 / 回滚操作都会记录操作人与时间（审计先行：审计写不进去，操作不执行）。</li>
        </ul>
      </div>
    </div>
  );
}
